import React from 'react'
import {Wrench, DollarSign, Calendar, TrendingDown} from 'lucide-react'
import { CountryConfig } from './types'
import { maintenanceCosts30Years, fallbackMaterialRates } from './constants'

interface MaintenanceComparisonProps {
  currentMaterial: string
  currentCountry: CountryConfig
  squareFootage: string
}

export default function MaintenanceComparison({
  currentMaterial,
  currentCountry,
  squareFootage
}: MaintenanceComparisonProps) {
  const sqft = parseFloat(squareFootage) || 0
  const squares = sqft / 100

  // All materials to compare
  const materials = [
    { id: 'asphalt', name: 'Asphalt Shingles' },
    { id: 'metal', name: 'Metal Roofing' },
    { id: 'tile', name: 'Clay/Concrete Tile' },
    { id: 'slate', name: 'Natural Slate' },
    { id: 'recycled_shingles', name: 'Recycled Shingles' },
    { id: 'solar_tiles', name: 'Solar Roof Tiles' },
    { id: 'green_roof', name: 'Green/Living Roof' },
    { id: 'recycled_metal', name: 'Recycled Metal' },
    { id: 'composite_slate', name: 'Composite Slate' },
  ]

  return (
    <div className="mt-8 bg-gradient-to-br from-blue-50 to-orange-50 border-2 border-blue-800 rounded-lg p-6 shadow-xl" role="region" aria-labelledby="maintenance-comparison">
      <div className="flex items-center gap-3 mb-6">
        <Wrench size={28} className="text-orange-500" aria-hidden="true" />
        <h3 id="maintenance-comparison" className="text-2xl font-bold text-gray-900">
          30-Year Maintenance Cost Comparison
        </h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">
        Total ownership costs include installation + maintenance over 30 years. Lower maintenance materials often offset higher upfront costs.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse bg-white rounded-lg overflow-hidden shadow-md">
          <thead>
            <tr className="bg-blue-800 text-white">
              <th className="text-left p-4 font-bold">Material</th>
              <th className="text-left p-4 font-bold">Installation Cost</th>
              <th className="text-left p-4 font-bold">30-Year Maintenance</th>
              <th className="text-left p-4 font-bold">Total 30-Year Cost</th>
              <th className="text-left p-4 font-bold">Maintenance Details</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((material) => {
              const isSelected = currentMaterial === material.id
              const rates = fallbackMaterialRates[material.id]
              const maintenanceCost = maintenanceCosts30Years[material.id]
              
              if (!rates || !maintenanceCost) return null

              const installLow = sqft * rates.low
              const installHigh = sqft * rates.high
              const installAvg = (installLow + installHigh) / 2
              
              const maintenanceTotal = maintenanceCost.total * squares
              const totalOwnership = installAvg + maintenanceTotal

              return (
                <tr 
                  key={material.id} 
                  className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                    isSelected ? 'bg-orange-100 border-l-4 border-l-orange-500 font-semibold' : ''
                  }`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className={isSelected ? 'text-orange-600 font-bold' : 'text-gray-900'}>
                        {material.name}
                      </span>
                      {isSelected && (
                        <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">Your Choice</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-gray-700">
                    {currentCountry.currencySymbol}{installAvg.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <DollarSign size={16} className="text-green-600" aria-hidden="true" />
                      <span className="font-semibold text-gray-700">
                        {currentCountry.currencySymbol}{maintenanceTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`text-lg font-bold ${
                      isSelected ? 'text-orange-600' : 'text-blue-800'
                    }`}>
                      {currentCountry.currencySymbol}{totalOwnership.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-gray-600">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Calendar size={12} className="text-gray-400" aria-hidden="true" />
                        <span>{maintenanceCost.frequency}</span>
                      </div>
                      <div className="text-gray-500">
                        Inspections: {currentCountry.currencySymbol}{(maintenanceCost.inspections * squares).toFixed(0)} • 
                        Repairs: {currentCountry.currencySymbol}{(maintenanceCost.repairs * squares).toFixed(0)} • 
                        Cleaning: {currentCountry.currencySymbol}{(maintenanceCost.cleaning * squares).toFixed(0)}
                      </div>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 bg-blue-100 border-l-4 border-blue-800 rounded">
        <div className="flex items-start gap-3">
          <TrendingDown size={20} className="text-blue-800 mt-1 flex-shrink-0" aria-hidden="true" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-2">Smart Investment Tip:</p>
            <p>
              Materials like Metal, Slate, and Recycled Metal have higher upfront costs but significantly lower maintenance over 30 years. 
              Consider total ownership cost, not just installation price, when choosing your roofing material.
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-4 text-center">
        * Maintenance costs are estimates based on industry averages for a {squareFootage || '0'} sq ft roof. 
        Actual costs vary by climate, installation quality, and local contractor rates.
      </p>
    </div>
  )
}
